import React, { useEffect, useRef, useState } from 'react';
import { formatDate, parseDisplayDate } from '../utils/dateFormat';

/**
 * Date box that always shows and accepts DD/MM/YYYY (the browser's own date box follows the
 * computer's language and cannot be forced). Drop-in for <input type="date">: `value` is
 * 'YYYY-MM-DD' and onChange gets an event-like { target: { value } } in the same format.
 * The calendar button opens the normal date picker.
 */
export default function DateInput({
  value, onChange, style, min, max, disabled, placeholder = 'DD/MM/YYYY', ...rest
}) {
  const show = (v) => (v ? formatDate(v, '') : '');
  const [text, setText] = useState(show(value));
  const picker = useRef(null);

  useEffect(() => { setText(show(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps

  const emit = (v) => { if (onChange) onChange({ target: { value: v } }); };

  const handleText = (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, 8);
    let out = digits;
    if (digits.length > 4) out = `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
    else if (digits.length > 2) out = `${digits.slice(0, 2)}/${digits.slice(2)}`;
    setText(out);
    if (out === '') emit('');
    else if (out.length === 10) {
      const iso = parseDisplayDate(out);
      if (iso) emit(iso);
    }
  };

  return (
    <span style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', maxWidth: '100%' }}>
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        maxLength={10}
        disabled={disabled}
        value={text}
        onChange={handleText}
        onBlur={() => setText(show(value))}
        style={{ ...style, paddingRight: 30, minWidth: 120 }}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-label="Open calendar"
        onClick={() => { const p = picker.current; if (p && p.showPicker) p.showPicker(); else if (p) p.focus(); }}
        style={{ position: 'absolute', right: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 2, color: 'var(--color-text-secondary)' }}
      >
        {'📅'}
      </button>
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        min={min}
        max={max}
        value={value || ''}
        onChange={(e) => emit(e.target.value)}
        style={{ position: 'absolute', right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
      />
    </span>
  );
}
